import { useCallback, useEffect, useRef } from 'react';
import { get, set } from 'idb-keyval';

import {
  AppSessionCreateMutationVariables,
  useAppSessionCreateMutation,
} from '@ui/mutations/appSessionCreate';
import { APP_SESSION_STATE_TITLE_KEY } from '@ui/components/student/screen/LearningScreen/constants';

import { MutationAppSessionCreateArgs, DashboardLogItemsInput } from '@mildang/graphql-artifact';
import { gql } from '@apollo/client';
import { useIdleTimer } from 'react-idle-timer';
import { Duration } from 'luxon';
import { APP_SESSION_TIMEOUT } from '@shared/constants/appSession';
import { UseLearningSession_StudyUnitInstanceFragment } from './useLearningSession.generated';
import getStudyActivityTitle from '@ui/utils/getStudyActivityTitle';
import getSUIType from '@ui/utils/getSUIType';
import { getStudyActivityTypeLabels } from '@ui/components/student/pattern/constants';
import { useLingui } from '@lingui/react';
import { I18n } from '@lingui/core';
import { getStudyUnitTitle } from '@ui/utils/getStudyUnitTitle';
import { findIndex } from 'lodash-es';
import { useInterval } from 'react-use';
import { usePageVisibility } from 'react-page-visibility';
import produce from 'immer';
import { useDebounceCallback } from '@react-hook/debounce';

const DEFAULT_INTERVAL_MILLISECONDS = 1000 * 60;
const DEBOUNCE_MILLISECONDS = 1000 * 2;

export type UseLearningSessionProps = {
  disableSend: boolean;
  currentStudyModuleInstanceId: string;
  studyUnitInstances: UseLearningSession_StudyUnitInstanceFragment[];
  currentStudyUnitInstanceId: string;
  groupId: string;
} & ({ currentActivityInstanceId: string } | { isUnitSummary: boolean });

function getAppSessionTitle(
  props: UseLearningSessionProps & {
    i18n: I18n;
  },
) {
  const { studyUnitInstances, currentStudyUnitInstanceId, i18n } = props;
  const studyUnitInstanceIndex = studyUnitInstances.findIndex(
    (studyUnitInstance) => studyUnitInstance.id === currentStudyUnitInstanceId,
  );
  const studyUnitInstance = studyUnitInstances?.[studyUnitInstanceIndex] || undefined;
  if (!studyUnitInstance) {
    return '';
  }
  const suiType = getSUIType(studyUnitInstance);
  const suiTitle = getStudyUnitTitle({ studyUnit: studyUnitInstance });

  if ('isUnitSummary' in props && props.isUnitSummary) {
    return `학습 요약 / ${suiTitle}`;
  } else if ('currentActivityInstanceId' in props && props.currentActivityInstanceId) {
    const saiIndex = findIndex(studyUnitInstance?.studyActivityInstances, [
      'id',
      props.currentActivityInstanceId,
    ]);
    const sai = studyUnitInstance?.studyActivityInstances[saiIndex];
    const saiType = getStudyActivityTypeLabels(i18n)[sai?.type ?? 'QUESTION'];
    let saiTitle = sai ? getStudyActivityTitle(sai) : '';

    if (suiType === 'WORD_TEST') {
      saiTitle = `Q${saiIndex + 1}`;
    }

    if (saiType && saiTitle && suiTitle) {
      return `${saiType} ${saiTitle} / ${suiTitle}`;
    }
  }

  return '';
}

const useLearningSession = (props: UseLearningSessionProps) => {
  const { reset, getTotalActiveTime, isIdle } = useIdleTimer({
    timeout: APP_SESSION_TIMEOUT,
  });

  const isVisible = usePageVisibility();

  const createAppSession = useAppSessionCreateMutation();

  const sendSessionCount = useRef<number>(0);
  const { i18n } = useLingui();

  const appSessionTitle = getAppSessionTitle({
    ...props,
    i18n,
  });

  const currentActivityInstanceId = (() => {
    if ('currentActivityInstanceId' in props && props.currentActivityInstanceId)
      return props.currentActivityInstanceId;
    else if ('isUnitSummary' in props && props.isUnitSummary)
      return props.studyUnitInstances?.[0]?.studyActivityInstances?.[0]?.id;
    else return undefined;
  })();

  const currentUnit = props.studyUnitInstances.find((unit) => unit.id === props.currentStudyUnitInstanceId);

  // 모아보기뷰 용 debounce AppSession
  const createAppSessionWithDebounce = useDebounceCallback(async () => {
    sendSession();
  }, DEBOUNCE_MILLISECONDS);

  const sendSession = useCallback(
    async (activityTime?: string) => {
      if (!appSessionTitle || props.disableSend || !isVisible) {
        return;
      }

      const createAppSessionVariables = await getActivityTimeLogInput({
        studyActivityInstanceId: currentActivityInstanceId,
        title: appSessionTitle,
        activityTime: activityTime && !activityTime.includes('-') ? activityTime : '00:00:00',
      });

      const createAppSessionVariablesWithGroupId = produce(createAppSessionVariables, (draft) => {
        draft.input.logs.groupId = props.groupId;
      });

      await createAppSession({
        variables: createAppSessionVariablesWithGroupId,
      });
    },
    [appSessionTitle, createAppSession, currentActivityInstanceId, props.disableSend, props.groupId],
  );

  const endActivity = useCallback(
    async (activityTime?: string) => {
      // time 확인
      if (currentUnit?.viewType === 'SINGLE_PAGE_VERTICAL' && activityTime === '00:00:00') {
        // 모아보기뷰에서 빠른스크롤로 인해 날라가는 의미없는 TOTAL_ACTIVITY_TIME_LOG appSession을 지워준다.
        reset();
      } else {
        await sendSession(activityTime);
      }
      reset();
      sendSessionCount.current = 0;
    },
    // 의도적으로 reset 이 누락되어있음.
    [sendSession, currentUnit],
  );

  useInterval(async () => {
    if ('currentActivityInstanceId' in props && props.currentActivityInstanceId && !isIdle() && isVisible) {
      await sendSession(
        Duration.fromObject({ milliseconds: DEFAULT_INTERVAL_MILLISECONDS }).toFormat('hh:mm:ss'),
      );
      sendSessionCount.current += 1;
    }
  }, DEFAULT_INTERVAL_MILLISECONDS);

  useEffect(() => {
    if ('currentActivityInstanceId' in props && props.currentActivityInstanceId) {
      const sendStartSessionTimer = setTimeout(() => {
        if (currentUnit?.viewType === 'SINGLE_PAGE_VERTICAL') {
          createAppSessionWithDebounce();
        } else {
          sendSession();
        }
        sendSessionCount.current = 0;
      }, 500);

      return () => {
        clearTimeout(sendStartSessionTimer);
        const milliseconds = Math.abs(
          getTotalActiveTime() - DEFAULT_INTERVAL_MILLISECONDS * sendSessionCount.current,
        );
        endActivity(Duration.fromObject({ milliseconds }).toFormat('hh:mm:ss'));
      };
    }
    // 의도적으로 getTotalActiveTime 이 누락되어있음
  }, [
    sendSession,
    endActivity,
    // @ts-ignore
    props?.currentActivityInstanceId,
    currentUnit,
    createAppSessionWithDebounce,
  ]);
};

export default useLearningSession;

gql`
  fragment UseLearningSession_StudyModuleInstance on StudyModuleInstance {
    id
  }
`;

gql`
  fragment UseLearningSession_StudyUnitInstance on StudyUnitInstance {
    id
    type
    studyActivityInstances {
      id
      type
      title
      ...getStudyActivityTitle_StudyActivityInstance @unmask
    }
    isEstimative
    viewDatas {
      __typename
    }
    viewType
    ...GetStudyUnitTitle_StudyUnitInstance @unmask
  }
`;

const getTitle = async (title?: string) => {
  const persistTitle: string | null = (await get(APP_SESSION_STATE_TITLE_KEY)) ?? null;
  return title || persistTitle || document.title;
};

export interface ActivityEventLogInputProps {
  connectionState: DashboardLogItemsInput['connectionState'];
  title?: DashboardLogItemsInput['title'];
}

// 학생 페이지에서의 상태 추적
// 세션 만료, 재진입, 페이지 종료, 포커스 인, 포커스 아웃 수집 시 사용
export const getActivityEventLogInput = async ({
  title,
  connectionState,
}: ActivityEventLogInputProps): Promise<MutationAppSessionCreateArgs> => {
  const type = 'ACTIVITY_EVENT_LOG';
  const createdAt = new Date().toISOString();
  const activityTime = '00:00:00';

  let resultTitle = '';

  try {
    resultTitle = await getTitle(title);

    await set(APP_SESSION_STATE_TITLE_KEY, resultTitle);
  } catch (e) {
    console.error(e);
  }

  return {
    input: {
      logs: {
        type,
        createdAt,
        items: {
          title: resultTitle,
          connectionState,
          activityTime,
          device: navigator.userAgent,
        },
      },
    },
  };
};

// Title은 페이지 변경 시 localStorage에 저장하기 때문에 optional
export interface ActivityStateLogInputProps {
  studyActivityInstanceId: DashboardLogItemsInput['studyActivityInstanceId'];
  activityState: DashboardLogItemsInput['activityState'];
  title?: DashboardLogItemsInput['title'];
}

// SA 별 activityState 추적
// 제출, 편집, 정답보기, 재도전, etc...
export const getActivityStateLogInput = async ({
  title,
  activityState,
  ...rest
}: ActivityStateLogInputProps): Promise<AppSessionCreateMutationVariables> => {
  const type = 'TOTAL_ACTIVITY_TIME_LOG';
  const createdAt = new Date().toISOString();
  const connectionState = 'ON';
  const activityTime = '00:00:00';

  let resultTitle = '';

  try {
    resultTitle = await getTitle(title);

    await set(APP_SESSION_STATE_TITLE_KEY, resultTitle);
  } catch (e) {
    console.error(e);
  }

  return {
    input: {
      logs: {
        type,
        createdAt,
        items: {
          title: resultTitle,
          connectionState,
          device: navigator.userAgent,
          activityTime,
          activityState,
          ...rest,
        },
      },
    },
  };
};

export interface ActivityTimeLogInputProps {
  studyActivityInstanceId: DashboardLogItemsInput['studyActivityInstanceId'];
  title: DashboardLogItemsInput['title'];
  activityState?: DashboardLogItemsInput['activityState'];
  activityTime?: DashboardLogItemsInput['activityTime'];
}

// SA 별 머문 시간 추적
export const getActivityTimeLogInput = async ({
  title,
  activityTime = '00:00:00',
  ...rest
}: ActivityTimeLogInputProps): Promise<AppSessionCreateMutationVariables> => {
  const type = 'TOTAL_ACTIVITY_TIME_LOG';
  const createdAt = new Date().toISOString();
  const connectionState = 'ON';

  let resultTitle = '';

  try {
    resultTitle = await getTitle(title);

    await set(APP_SESSION_STATE_TITLE_KEY, resultTitle);
  } catch (e) {
    console.error(e);
  }

  return {
    input: {
      logs: {
        type,
        createdAt,
        items: {
          title: resultTitle,
          connectionState,
          device: navigator.userAgent,
          activityTime,
          ...rest,
        },
      },
    },
  };
};
